import { supabase } from '../supabaseClient';
import { PRODUCTION_SITE_ORIGIN } from '../constants/publicSite';
import {
  normalizeChallengeContent,
  normalizeChallengeThemeColor,
  challengePhotoThumbPath,
  type ChallengeAnswer,
  type ChallengeContent,
  type ChallengeItem,
  type ChallengeMission,
  type ChallengeProject,
  type ChallengeProjectIntro,
  type ChallengeSubmission,
} from '../types/assignmentChallenge';
import { deleteAssignmentCoverImage } from './assignmentCoverImageStorageService';
import {
  clearAllChallengePhotoCaches,
  clearChallengePhotoObjectUrls,
  deleteCachedPhotos,
  listMissingPhotos,
  markPhotosMissing,
  photoDeviceCacheSupported,
  readCachedPhotos,
  storePhotos,
} from './challengePhotoDeviceCache';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

export const CHALLENGE_PHOTO_BUCKET = 'assignment-challenge-photos';

/** 사용자별 캐시: 키에 `${uid}.` 구간을 둔다 */
const BUNDLE_CACHE_PREFIX = 'cp.assignmentChallenge.bundle.v2.';
const SUBS_CACHE_PREFIX = 'cp.assignmentChallenge.subs.v2.';
const ITEM_SUBS_CACHE_PREFIX = 'cp.assignmentChallenge.itemSubs.v2.';
const ITEM_SUBS_INDEX_PREFIX = 'cp.assignmentChallenge.itemSubsIndex.v2.';
const LEGACY_CACHE_PREFIXES = [
  'cp.assignmentChallenge.bundle.v1.',
  'cp.assignmentChallenge.subs.v1.',
  'cp.assignmentChallenge.itemSubs.v1.',
  'cp.assignmentChallenge.itemSubsIndex.v1',
  'cp.assignmentChallenge.staleSweep.v1.',
];
const ITEM_SUBS_CACHE_LIMIT = 8;
/** 예전(7일 서명 URL) 보관 키. 로그아웃 시 함께 지운다 */
const SIGNED_URL_CACHE_KEY = 'cp.assignmentChallenge.signedUrls.v1';
const SIGNED_URL_TTL_SEC = 600;
const STALE_SWEEP_KEY_PREFIX = 'cp.assignmentChallenge.staleSweep.v2.';
const STALE_SWEEP_INTERVAL_MS = 6 * 3600 * 1000;

export type ChallengeBundle = { project: ChallengeProject; items: ChallengeItem[] };

export type ChallengeSubmissionSummary = {
  projectId: string;
  itemId: string;
  memberId: string;
  submittedAt: string;
};

function mapProject(row: Record<string, unknown>): ChallengeProject {
  return {
    id: String(row.id ?? ''),
    workspaceId: String(row.workspace_id ?? ''),
    gradeId: String(row.grade_id ?? ''),
    assignmentItemId: String(row.assignment_item_id ?? ''),
    ownerUserId: String(row.owner_user_id ?? ''),
    title: String(row.title ?? ''),
    shareToken: String(row.share_token ?? ''),
    shareEnabled: row.share_enabled !== false,
    themeColor: normalizeChallengeThemeColor(typeof row.theme_color === 'string' ? row.theme_color : ''),
    intro:
      row.intro && typeof row.intro === 'object' && Object.keys(row.intro as object).length > 0
        ? (row.intro as Partial<ChallengeProjectIntro>)
        : null,
    updatedAt: String(row.updated_at ?? ''),
  };
}

function mapItem(row: Record<string, unknown>): ChallengeItem {
  return {
    id: String(row.id ?? ''),
    projectId: String(row.project_id ?? ''),
    sortOrder: Number(row.sort_order) || 0,
    isVisible: row.is_visible !== false,
    title: String(row.title ?? ''),
    content: normalizeChallengeContent(row.content),
    thumbnailUrl: typeof row.thumbnail_url === 'string' && row.thumbnail_url ? row.thumbnail_url : null,
    thumbnailPath: typeof row.thumbnail_path === 'string' && row.thumbnail_path ? row.thumbnail_path : null,
    revision: Number(row.revision) || 1,
    updatedAt: String(row.updated_at ?? ''),
  };
}

/** intro 마이그레이션(20261016120000) 적용 전에도 저장이 막히지 않게 한다 */
function isMissingIntroColumn(message: string): boolean {
  return /\bintro\b/i.test(message);
}

/** theme_color 마이그레이션(20261014160000) 적용 전에도 생성·수정이 막히지 않게 한다 */
function isMissingThemeColumn(message: string): boolean {
  return /theme_color/i.test(message);
}

async function cacheUid(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? 'anon';
}

async function bundleCacheKey(projectId: string): Promise<string> {
  return `${BUNDLE_CACHE_PREFIX}${await cacheUid()}.${projectId}`;
}

function readCache<T>(key: string): T | null {
  try {
    const raw = deviceCacheStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: unknown): void {
  try {
    deviceCacheStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

function removeCache(key: string): void {
  try {
    deviceCacheStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** 자동 로그아웃 시: 기기 캐시(사용자별)는 두고 메모리 상태만 비운다 */
export function clearAssignmentChallengeMemoryCaches(): void {
  signedUrlCache.clear();
  clearChallengePhotoObjectUrls();
}

export function clearAssignmentChallengeCaches(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (
        k &&
        (k.startsWith(BUNDLE_CACHE_PREFIX) ||
          k.startsWith(SUBS_CACHE_PREFIX) ||
          k.startsWith(ITEM_SUBS_CACHE_PREFIX) ||
          k.startsWith(STALE_SWEEP_KEY_PREFIX) ||
          k.startsWith(ITEM_SUBS_INDEX_PREFIX) ||
          LEGACY_CACHE_PREFIXES.some((prefix) => k.startsWith(prefix)) ||
          k === SIGNED_URL_CACHE_KEY)
      ) {
        keys.push(k);
      }
    }
    keys.forEach((k) => deviceCacheStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  signedUrlCache.clear();
  void clearAllChallengePhotoCaches();
}

export function formatChallengeError(e: unknown): string {
  const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  if (/QUOTA_EXCEEDED/i.test(msg)) return '이미지 저장 용량이 가득 찼습니다.';
  if (/assignment_challenge|does not exist|schema cache/i.test(msg)) {
    return '챌린지 DB가 아직 준비되지 않았습니다. 마이그레이션(20261014120000)을 적용해 주세요.';
  }
  if (/not a workspace member|permission|row-level/i.test(msg)) return '이 클래스에 대한 권한이 없습니다.';
  return msg || '챌린지 요청에 실패했습니다.';
}

const CHALLENGE_PUBLIC_SITE_ORIGIN = (
  process.env.REACT_APP_PUBLIC_SITE_URL || PRODUCTION_SITE_ORIGIN
).replace(/\/$/, '');

/** 개발 서버(로컬)에서는 운영 사이트에 아직 없는 페이지일 수 있어 현재 접속 주소로 만든다 */
function challengePublicOrigin(): string {
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    return window.location.origin;
  }
  return CHALLENGE_PUBLIC_SITE_ORIGIN;
}

export function buildChallengePublicUrl(shareToken: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${challengePublicOrigin()}${base}/c/${encodeURIComponent(shareToken)}`;
}

// ───────────────────────── 프로젝트 · 챌린지 ─────────────────────────

/** LS 캐시 즉시 반환 + project.updated_at head 비교 후 다를 때만 풀 로드 */
export async function fetchChallengeBundle(
  projectId: string,
  opts?: { force?: boolean; onCached?: (bundle: ChallengeBundle) => void }
): Promise<ChallengeBundle | null> {
  const key = await bundleCacheKey(projectId);
  const cached = opts?.force ? null : readCache<ChallengeBundle>(key);
  if (cached) opts?.onCached?.(cached);

  const { data: head, error: headErr } = await supabase
    .from('assignment_challenge_projects')
    .select('id, updated_at')
    .eq('id', projectId)
    .maybeSingle();
  if (headErr) throw new Error(formatChallengeError(headErr.message));
  if (!head) {
    removeCache(key);
    return null;
  }
  if (cached && cached.project.updatedAt === String((head as { updated_at?: string }).updated_at ?? '')) {
    return cached;
  }

  const [{ data: projectRow, error: pErr }, { data: itemRows, error: iErr }] = await Promise.all([
    supabase.from('assignment_challenge_projects').select('*').eq('id', projectId).maybeSingle(),
    supabase
      .from('assignment_challenge_items')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true }),
  ]);
  if (pErr) throw new Error(formatChallengeError(pErr.message));
  if (iErr) throw new Error(formatChallengeError(iErr.message));
  if (!projectRow) return null;
  const bundle: ChallengeBundle = {
    project: mapProject(projectRow as Record<string, unknown>),
    items: ((itemRows ?? []) as Record<string, unknown>[]).map(mapItem),
  };
  writeCache(key, bundle);
  return bundle;
}

export async function createChallengeProject(params: {
  workspaceId: string;
  gradeId: string;
  assignmentItemId: string;
  title: string;
  themeColor?: string;
}): Promise<ChallengeProject> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');
  const base = {
    workspace_id: params.workspaceId,
    grade_id: params.gradeId,
    assignment_item_id: params.assignmentItemId,
    owner_user_id: uid,
    title: params.title.slice(0, 200),
  };
  const theme = normalizeChallengeThemeColor(params.themeColor);
  const insert = (row: Record<string, unknown>) =>
    supabase.from('assignment_challenge_projects').insert(row).select('*').single();
  let { data, error } = await insert(theme ? { ...base, theme_color: theme } : base);
  if (error && theme && isMissingThemeColumn(error.message)) ({ data, error } = await insert(base));
  if (error) throw new Error(formatChallengeError(error.message));
  return mapProject(data as Record<string, unknown>);
}

function contentForDb(content: ChallengeContent): ChallengeContent {
  return {
    ...content,
    missions: content.missions.map((m, i) => ({ ...m, id: m.id || `ms-${i}` })),
  };
}

/** 편집 모달 저장: 챌린지 upsert(내용 바뀐 것만 revision+1) · 제거된 챌린지 삭제 · 프로젝트 updated_at 갱신 */
export async function saveChallengeItems(params: {
  project: ChallengeProject;
  title: string;
  intro?: ChallengeProjectIntro;
  items: ChallengeItem[];
  previous: ChallengeItem[];
}): Promise<ChallengeBundle & { introSkipped?: boolean }> {
  const { project, items, previous } = params;
  const prevById = new Map(previous.map((i) => [i.id, i]));
  const now = new Date().toISOString();
  const rows = items.map((item, index) => {
    const prev = prevById.get(item.id);
    const changed =
      !prev ||
      prev.title !== item.title ||
      JSON.stringify(prev.content) !== JSON.stringify(item.content);
    return {
      id: item.id,
      project_id: project.id,
      sort_order: index,
      is_visible: item.isVisible,
      title: item.title.slice(0, 200),
      content: contentForDb(item.content),
      thumbnail_url: item.thumbnailUrl,
      thumbnail_path: item.thumbnailPath,
      revision: prev ? (changed ? prev.revision + 1 : prev.revision) : 1,
      updated_at: now,
    };
  });
  const nextIds = new Set(items.map((i) => i.id));
  const removed = previous.filter((p) => !nextIds.has(p.id));

  if (removed.length > 0) {
    await removeChallengePhotos(project.id, { itemIds: removed.map((r) => r.id) });
    const { error: delErr } = await supabase
      .from('assignment_challenge_items')
      .delete()
      .in(
        'id',
        removed.map((r) => r.id)
      );
    if (delErr) throw new Error(formatChallengeError(delErr.message));
  }
  if (rows.length > 0) {
    const { error } = await supabase.from('assignment_challenge_items').upsert(rows, { onConflict: 'id' });
    if (error) throw new Error(formatChallengeError(error.message));
  }
  const projectRow: Record<string, unknown> = { title: params.title.slice(0, 200), updated_at: now };
  if (params.intro) {
    projectRow.intro = {
      eyebrow: params.intro.eyebrow.slice(0, 60),
      subtitle: params.intro.subtitle.slice(0, 80),
      headline: params.intro.headline.slice(0, 120),
      description: params.intro.description.slice(0, 2000),
      topLogoText: params.intro.topLogoText.slice(0, 60),
      bottomLogoText: params.intro.bottomLogoText.slice(0, 60),
      thumbnailUrl: params.intro.thumbnailUrl,
      thumbnailPath: params.intro.thumbnailPath,
    };
  }
  const updateProject = (row: Record<string, unknown>) =>
    supabase.from('assignment_challenge_projects').update(row).eq('id', project.id);
  let introSkipped = false;
  let { error: pErr } = await updateProject(projectRow);
  if (pErr && 'intro' in projectRow && isMissingIntroColumn(pErr.message)) {
    const { intro: _omit, ...rest } = projectRow;
    introSkipped = true;
    ({ error: pErr } = await updateProject(rest));
  }
  if (pErr) throw new Error(formatChallengeError(pErr.message));

  const keepThumbs = new Set(items.map((i) => i.thumbnailPath).filter(Boolean) as string[]);
  const dropThumbs = previous
    .map((p) => p.thumbnailPath)
    .filter((p): p is string => !!p && !keepThumbs.has(p));
  await Promise.all(dropThumbs.map((p) => deleteAssignmentCoverImage(p).catch(() => undefined)));

  const bundle = await fetchChallengeBundle(project.id, { force: true });
  if (!bundle) throw new Error('챌린지를 다시 불러오지 못했습니다.');
  return introSkipped ? { ...bundle, introSkipped } : bundle;
}

/** coverUrl: 과제 썸네일(정사각) URL을 참여 화면 설명의 썸네일로 맞춘다(빈 값이면 해제) */
export async function updateChallengeProjectMeta(
  projectId: string,
  patch: { title?: string; shareEnabled?: boolean; themeColor?: string; coverUrl?: string | null }
): Promise<void> {
  let row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.title != null) row.title = patch.title.slice(0, 200);
  if (patch.shareEnabled != null) row.share_enabled = patch.shareEnabled;
  if (patch.themeColor != null) row.theme_color = normalizeChallengeThemeColor(patch.themeColor);
  if (patch.coverUrl !== undefined) {
    const { data, error: readErr } = await supabase
      .from('assignment_challenge_projects')
      .select('intro')
      .eq('id', projectId)
      .maybeSingle();
    if (!readErr) {
      const cur = (data as { intro?: unknown } | null)?.intro;
      row.intro = {
        ...(cur && typeof cur === 'object' ? (cur as Record<string, unknown>) : {}),
        thumbnailUrl: patch.coverUrl || null,
        thumbnailPath: null,
      };
    }
  }
  const update = (r: Record<string, unknown>) =>
    supabase.from('assignment_challenge_projects').update(r).eq('id', projectId);
  let { error } = await update(row);
  if (error && 'intro' in row && isMissingIntroColumn(error.message)) {
    const { intro: _omitIntro, ...rest } = row;
    row = rest;
    if (Object.keys(row).length <= 1) return;
    ({ error } = await update(row));
  }
  if (error && 'theme_color' in row && isMissingThemeColumn(error.message)) {
    const { theme_color: _omit, ...rest } = row;
    if (Object.keys(rest).length <= 1) return;
    ({ error } = await update(rest));
  }
  if (error) throw new Error(formatChallengeError(error.message));
  removeCache(await bundleCacheKey(projectId));
}

export async function regenerateChallengeShareToken(projectId: string): Promise<string> {
  const { data, error } = await supabase.rpc('challenge_regenerate_share_token', {
    p_project_id: projectId,
  });
  if (error) throw new Error(formatChallengeError(error.message));
  removeCache(await bundleCacheKey(projectId));
  return String(data ?? '');
}

/** 복제: 원본 챌린지 내용을 새 프로젝트로 복사(새 하위 과제 id 순서대로 매핑, 썸네일 URL 공유 안 함) */
export async function duplicateChallengeItems(params: {
  sourceProjectId: string;
  target: ChallengeProject;
  targetItemIds: string[];
  titles: string[];
}): Promise<void> {
  const src = await fetchChallengeBundle(params.sourceProjectId, { force: true });
  if (!src) return;
  const rows = params.targetItemIds.map((id, index) => {
    const from = src.items[index];
    return {
      id,
      project_id: params.target.id,
      sort_order: index,
      is_visible: from?.isVisible ?? true,
      title: (params.titles[index] ?? from?.title ?? '').slice(0, 200),
      content: from ? contentForDb(from.content) : {},
      thumbnail_url: null,
      thumbnail_path: null,
      revision: 1,
    };
  });
  if (rows.length === 0) return;
  const { error } = await supabase.from('assignment_challenge_items').upsert(rows, { onConflict: 'id' });
  if (error) throw new Error(formatChallengeError(error.message));
}

/** 하위 과제 제목·순서를 과제노트에서 바꾼 경우 챌린지 행에 반영(내용은 유지, 없는 챌린지는 기본 내용으로 생성) */
export async function syncChallengeItemsFromSubTasks(params: {
  projectId: string;
  subTasks: Array<{ id: string; title: string }>;
  defaultContent: () => ChallengeContent;
}): Promise<void> {
  const bundle = await fetchChallengeBundle(params.projectId, { force: true });
  if (!bundle) return;
  const existing = new Map(bundle.items.map((i) => [i.id, i]));
  const nextItems: ChallengeItem[] = params.subTasks.map((s, index) => {
    const prev = existing.get(s.id);
    return prev
      ? { ...prev, title: s.title, sortOrder: index }
      : {
          id: s.id,
          projectId: params.projectId,
          sortOrder: index,
          isVisible: true,
          title: s.title,
          content: params.defaultContent(),
          thumbnailUrl: null,
          thumbnailPath: null,
          revision: 1,
          updatedAt: '',
        };
  });
  await saveChallengeItems({
    project: bundle.project,
    title: bundle.project.title,
    items: nextItems,
    previous: bundle.items,
  });
}

// ───────────────────────── 사진 정리 · 삭제 ─────────────────────────

async function removeStoragePaths(originals: string[]): Promise<void> {
  const paths = originals.flatMap((p) => [p, challengePhotoThumbPath(p)]);
  forgetSignedUrls(paths);
  await deleteCachedPhotos(paths).catch(() => undefined);
  for (let i = 0; i < paths.length; i += 100) {
    const slice = paths.slice(i, i + 100);
    const { error } = await supabase.storage.from(CHALLENGE_PHOTO_BUCKET).remove(slice);
    if (error && !/not.?found/i.test(error.message ?? '')) throw error;
  }
}

async function releasePhotoPaths(paths: string[]): Promise<void> {
  for (let i = 0; i < paths.length; i += 200) {
    const { error } = await supabase.rpc('challenge_release_photos', { p_paths: paths.slice(i, i + 200) });
    if (error) throw new Error(formatChallengeError(error.message));
  }
}

/** 파일 삭제 → 메타 삭제(용량 반환). 범위: 프로젝트 전체 / 특정 챌린지 / 정리 대상(orphan·만료 pending)만 */
export async function removeChallengePhotos(
  projectId: string,
  scope?: { itemIds?: string[]; staleOnly?: boolean }
): Promise<number> {
  const staleBefore = Date.now() - 30 * 60 * 1000;
  const sweepKey = `${STALE_SWEEP_KEY_PREFIX}${await cacheUid()}.${projectId}`;
  if (scope?.staleOnly) {
    const last = Number(readCache<number>(sweepKey)) || 0;
    if (Date.now() - last < STALE_SWEEP_INTERVAL_MS) return 0;
  }
  let query = supabase
    .from('assignment_challenge_photos')
    .select('path, status, expires_at')
    .eq('project_id', projectId);
  if (scope?.itemIds?.length) query = query.in('item_id', scope.itemIds);
  if (scope?.staleOnly) {
    query = query.or(
      `status.eq.orphan,and(status.eq.pending,expires_at.lt.${new Date(staleBefore).toISOString()})`
    );
  }
  const { data, error } = await query;
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return 0;
    throw new Error(formatChallengeError(error.message));
  }
  if (scope?.staleOnly) writeCache(sweepKey, Date.now());
  const rows = (data ?? []) as Array<{ path: string; status: string; expires_at: string }>;
  const paths = rows
    .filter((r) =>
      scope?.staleOnly
        ? r.status === 'orphan' || (r.status === 'pending' && Date.parse(r.expires_at) < staleBefore)
        : true
    )
    .map((r) => r.path);
  if (paths.length === 0) return 0;
  await removeStoragePaths(paths);
  await releasePhotoPaths(paths);
  return paths.length;
}

async function removeItemThumbnails(projectIds: string[]): Promise<void> {
  if (projectIds.length === 0) return;
  const { data } = await supabase
    .from('assignment_challenge_items')
    .select('thumbnail_path')
    .in('project_id', projectIds);
  const { data: introRows } = await supabase
    .from('assignment_challenge_projects')
    .select('intro')
    .in('id', projectIds);
  const introPaths = ((introRows ?? []) as Array<{ intro?: { thumbnailPath?: unknown } | null }>).map((r) =>
    typeof r.intro?.thumbnailPath === 'string' ? r.intro.thumbnailPath : null
  );
  const paths = [
    ...((data ?? []) as Array<{ thumbnail_path: string | null }>).map((r) => r.thumbnail_path),
    ...introPaths,
  ].filter((p): p is string => !!p);
  await Promise.all(paths.map((p) => deleteAssignmentCoverImage(p).catch(() => undefined)));
}

export async function deleteChallengeProjects(projectIds: string[]): Promise<void> {
  const ids = projectIds.filter(Boolean);
  for (const id of ids) {
    await removeChallengePhotos(id);
  }
  await removeItemThumbnails(ids);
  if (ids.length > 0) {
    const { error } = await supabase.from('assignment_challenge_projects').delete().in('id', ids);
    if (error && !/does not exist|schema cache/i.test(error.message ?? '')) {
      throw new Error(formatChallengeError(error.message));
    }
  }
  const bundleKeys = await Promise.all(ids.map((id) => bundleCacheKey(id)));
  bundleKeys.forEach((k) => removeCache(k));
}

/** 클래스 삭제 전: 해당 클래스 챌린지의 사진·썸네일까지 정리 */
export async function deleteChallengeProjectsForGrade(workspaceId: string, gradeId: string): Promise<void> {
  const { data, error } = await supabase
    .from('assignment_challenge_projects')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (error) return;
  await deleteChallengeProjects(((data ?? []) as Array<{ id: string }>).map((r) => r.id));
}

// ───────────────────────── 제출 ─────────────────────────

type SubsCache = { signature: string; rows: ChallengeSubmissionSummary[] };

/** 클래스의 챌린지 제출 요약(학생·챌린지별 제출 시각). count+max(updated_at) head 비교 후 변경 시만 풀 로드 */
export async function fetchChallengeSubmissionSummaries(params: {
  workspaceId: string;
  gradeId: string;
  projectIds: string[];
  force?: boolean;
}): Promise<ChallengeSubmissionSummary[]> {
  const ids = Array.from(new Set(params.projectIds.filter(Boolean))).sort();
  if (ids.length === 0) return [];
  const key = `${SUBS_CACHE_PREFIX}${await cacheUid()}.${params.workspaceId}.${params.gradeId}`;
  const cached = params.force ? null : readCache<SubsCache>(key);

  const { data: headRows, count, error: headErr } = await supabase
    .from('assignment_challenge_submissions')
    .select('updated_at', { count: 'exact' })
    .in('project_id', ids)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (headErr) {
    if (/does not exist|schema cache/i.test(headErr.message ?? '')) return [];
    throw new Error(formatChallengeError(headErr.message));
  }
  const maxUpdated = String((headRows as Array<{ updated_at?: string }> | null)?.[0]?.updated_at ?? '');
  const signature = `${ids.join(',')}|${count ?? 0}|${maxUpdated}`;
  if (cached && cached.signature === signature) return cached.rows;

  const { data, error } = await supabase
    .from('assignment_challenge_submissions')
    .select('project_id, item_id, member_id, submitted_at')
    .in('project_id', ids);
  if (error) throw new Error(formatChallengeError(error.message));
  const rows = ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    projectId: String(r.project_id ?? ''),
    itemId: String(r.item_id ?? ''),
    memberId: String(r.member_id ?? ''),
    submittedAt: String(r.submitted_at ?? ''),
  }));
  writeCache(key, { signature, rows } satisfies SubsCache);
  return rows;
}

function mapAnswer(raw: unknown): ChallengeAnswer | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    missionId: String(o.missionId ?? ''),
    text: typeof o.text === 'string' ? o.text : '',
    photoPath: typeof o.photoPath === 'string' && o.photoPath ? o.photoPath : null,
  };
}

type ItemSubsCache = { signature: string; rows: ChallengeSubmission[] };

function touchItemSubsIndex(uid: string, key: string): void {
  const indexKey = `${ITEM_SUBS_INDEX_PREFIX}${uid}`;
  const index = (readCache<string[]>(indexKey) ?? []).filter((k) => k !== key);
  index.push(key);
  while (index.length > ITEM_SUBS_CACHE_LIMIT) {
    const drop = index.shift();
    if (drop) removeCache(drop);
  }
  writeCache(indexKey, index);
}

/** 챌린지 하나의 제출 본문. LS 캐시 즉시 반환 + count·max(updated_at) head 비교 후 다를 때만 풀 로드 */
export async function fetchChallengeSubmissionsForItem(
  projectId: string,
  itemId: string,
  opts?: { force?: boolean; onCached?: (rows: ChallengeSubmission[]) => void }
): Promise<ChallengeSubmission[]> {
  const uid = await cacheUid();
  const key = `${ITEM_SUBS_CACHE_PREFIX}${uid}.${projectId}.${itemId}`;
  const cached = opts?.force ? null : readCache<ItemSubsCache>(key);
  if (cached) opts?.onCached?.(cached.rows);

  const { data: headRows, count, error: headErr } = await supabase
    .from('assignment_challenge_submissions')
    .select('updated_at', { count: 'exact' })
    .eq('project_id', projectId)
    .eq('item_id', itemId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (headErr) throw new Error(formatChallengeError(headErr.message));
  const maxUpdated = String((headRows as Array<{ updated_at?: string }> | null)?.[0]?.updated_at ?? '');
  const signature = `${count ?? 0}|${maxUpdated}`;
  if (cached && cached.signature === signature) {
    touchItemSubsIndex(uid, key);
    return cached.rows;
  }

  const { data, error } = await supabase
    .from('assignment_challenge_submissions')
    .select('*')
    .eq('project_id', projectId)
    .eq('item_id', itemId)
    .order('submitted_at', { ascending: false });
  if (error) throw new Error(formatChallengeError(error.message));
  const rows = mapSubmissionRows((data ?? []) as Array<Record<string, unknown>>);
  writeCache(key, { signature, rows } satisfies ItemSubsCache);
  touchItemSubsIndex(uid, key);
  return rows;
}

function mapSubmissionRows(data: Array<Record<string, unknown>>): ChallengeSubmission[] {
  return data.map((r) => {
    const snap = (r.item_snapshot && typeof r.item_snapshot === 'object'
      ? r.item_snapshot
      : {}) as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      projectId: String(r.project_id ?? ''),
      itemId: String(r.item_id ?? ''),
      memberId: String(r.member_id ?? ''),
      memberNameSnapshot: String(r.member_name_snapshot ?? ''),
      answers: (Array.isArray(r.answers) ? r.answers : [])
        .map(mapAnswer)
        .filter((a): a is ChallengeAnswer => a != null),
      itemSnapshot: {
        title: String(snap.title ?? ''),
        missions: normalizeChallengeContent({ missions: snap.missions }).missions as ChallengeMission[],
      },
      submittedAt: String(r.submitted_at ?? ''),
      updatedAt: String(r.updated_at ?? ''),
    };
  });
}

/** 기록 초기화: 해당 챌린지의 제출과 연결 사진만 삭제 */
export async function resetChallengeItemSubmissions(projectId: string, itemId: string): Promise<void> {
  removeCache(`${ITEM_SUBS_CACHE_PREFIX}${await cacheUid()}.${projectId}.${itemId}`);
  await removeChallengePhotos(projectId, { itemIds: [itemId] });
  const { error } = await supabase
    .from('assignment_challenge_submissions')
    .delete()
    .eq('project_id', projectId)
    .eq('item_id', itemId);
  if (error) throw new Error(formatChallengeError(error.message));
}

type SignedUrlEntry = { url: string; expiresAt: number };

/** 서명 URL은 기기에 없는 사진을 처음 내려받을 때만 쓰므로 짧게 발급하고 메모리에만 둔다 */
const signedUrlCache = new Map<string, SignedUrlEntry>();

function forgetSignedUrls(paths: string[]): void {
  paths.forEach((p) => signedUrlCache.delete(p));
}

/** 서명 URL 발급. 서버에 파일이 없으면 결과에서 빠진다 */
async function signChallengePhotoPaths(paths: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const now = Date.now();
  const need: string[] = [];
  Array.from(new Set(paths)).forEach((p) => {
    const hit = signedUrlCache.get(p);
    if (hit && hit.expiresAt - 60 * 1000 > now) out[p] = hit.url;
    else need.push(p);
  });
  if (need.length === 0) return out;
  const { data, error } = await supabase.storage
    .from(CHALLENGE_PHOTO_BUCKET)
    .createSignedUrls(need, SIGNED_URL_TTL_SEC);
  if (error) throw new Error(formatChallengeError(error.message));
  (data ?? []).forEach((row) => {
    if (row.path && row.signedUrl) {
      out[row.path] = row.signedUrl;
      signedUrlCache.set(row.path, { url: row.signedUrl, expiresAt: now + SIGNED_URL_TTL_SEC * 1000 });
    }
  });
  return out;
}

async function downloadBlobs(urls: Record<string, string>): Promise<Record<string, Blob>> {
  const entries = Object.entries(urls);
  const out: Record<string, Blob> = {};
  let cursor = 0;
  const worker = async () => {
    while (cursor < entries.length) {
      const [path, url] = entries[cursor];
      cursor += 1;
      try {
        const res = await fetch(url);
        if (res.ok) out[path] = await res.blob();
      } catch {
        /* 실패한 경로는 결과에서 빠진다 */
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(6, entries.length) }, worker));
  return out;
}

/** 기기 보관소 → 없으면 서명 URL로 내려받아 보관. 보관소를 못 쓰는 환경은 서명 URL을 그대로 쓴다 */
async function loadChallengePhotos(paths: string[]): Promise<{ urls: Record<string, string>; missing: string[] }> {
  const unique = Array.from(new Set(paths));
  if (unique.length === 0) return { urls: {}, missing: [] };
  if (!photoDeviceCacheSupported()) {
    const signed = await signChallengePhotoPaths(unique);
    return { urls: signed, missing: unique.filter((p) => !signed[p]) };
  }
  const urls = await readCachedPhotos(unique);
  const need = unique.filter((p) => !urls[p]);
  if (need.length === 0) return { urls, missing: [] };
  const signed = await signChallengePhotoPaths(need);
  const blobs = await downloadBlobs(signed);
  Object.assign(
    urls,
    await storePhotos(Object.entries(blobs).map(([path, blob]) => ({ path, blob })))
  );
  const missing = need.filter((p) => !signed[p]);
  need.forEach((p) => {
    if (!urls[p] && signed[p]) urls[p] = signed[p];
  });
  return { urls, missing };
}

/** 상세 창용 원본 */
export async function getChallengePhotoFullUrls(paths: string[]): Promise<Record<string, string>> {
  return (await loadChallengePhotos(paths)).urls;
}

/** 카드 이미지 로드 실패 시: 썸네일은 없음으로 표시하고 원본을 새로 받는다 */
export async function recoverChallengePhotoUrl(path: string): Promise<string | null> {
  const thumb = challengePhotoThumbPath(path);
  forgetSignedUrls([thumb, path]);
  await deleteCachedPhotos([thumb, path]);
  await markPhotosMissing([thumb]);
  const { urls } = await loadChallengePhotos([path]);
  return urls[path] ?? null;
}

/** 카드용: 썸네일, 썸네일이 없는 예전 사진은 원본으로 대체 */
export async function getChallengePhotoCardUrls(paths: string[]): Promise<Record<string, string>> {
  const unique = Array.from(new Set(paths));
  const thumbOf = new Map(unique.map((p) => [p, challengePhotoThumbPath(p)]));
  const knownMissing = await listMissingPhotos(Array.from(thumbOf.values()));
  const withThumb = unique.filter((p) => !knownMissing.has(thumbOf.get(p) as string));
  const { urls: thumbs, missing } = await loadChallengePhotos(withThumb.map((p) => thumbOf.get(p) as string));
  await markPhotosMissing(missing);
  const out: Record<string, string> = {};
  const fallback: string[] = [];
  unique.forEach((p) => {
    const t = thumbs[thumbOf.get(p) as string];
    if (t) out[p] = t;
    else fallback.push(p);
  });
  if (fallback.length > 0) Object.assign(out, (await loadChallengePhotos(fallback)).urls);
  return out;
}

(function removeLegacyChallengeCaches() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (k && (k === SIGNED_URL_CACHE_KEY || LEGACY_CACHE_PREFIXES.some((prefix) => k.startsWith(prefix)))) {
        keys.push(k);
      }
    }
    keys.forEach((k) => deviceCacheStorage.removeItem(k));
  } catch {
    /* ignore */
  }
})();
